import { useState } from 'react';
import type { SiteId, TaskId } from '@interfaces/IScheduler';
import { useSchedulerActions } from '../../hooks/useSchedulerStore';
import { TaskProgressSection } from './TaskProgressSection';
import { TaskPromiseRows } from './TaskPromiseRows';
import { useTaskStep } from '../../hooks/useTaskStep';
import { TaskStepHint } from './TaskStep';
import styles from './TaskCommitBlock.module.css';

// ---------------------------------------------------------------------------
// TaskCommitBlock — the promise (2026-09-18 commit UX design, "Drawer",
// amended 2026-09-20 and 2026-09-23). It opens the Overview tab: how a
// committed task is going (the board), what it promised (the rows), and what
// stands between the task and its next step.
//
// Where the task STANDS is not here: `TaskLadderTrail` renders Planned ──
// Committed ── Done in the drawer header, above the tabs, because that fact is
// true of the task rather than of this tab. Nor is the step's BUTTON: the rows
// read as a form, so their submit is the drawer footer's (`TaskStepActions`).
// ---------------------------------------------------------------------------

interface ITaskCommitBlockProps {
  siteId: SiteId;
  taskId: TaskId;
  scheduleNumber?: string;
}

export function TaskCommitBlock({ siteId, taskId, scheduleNumber }: ITaskCommitBlockProps) {
  // The area map, unfolded under the rows by the Areas row's toggle.
  const [areasOpen, setAreasOpen] = useState(false);
  const { openCommitDialog } = useSchedulerActions();
  const state = useTaskStep(siteId, taskId);
  if (!state) return null;

  const { task, leaves, step } = state;
  const isParent = leaves.length > 0;
  // A parent has no promise of its own (its leaves do), and a MILESTONE is not
  // promised at all: it is a date somebody either hit or did not. Neither gets
  // the ladder or the promise rows — a milestone gets one big Done instead
  // (2026-09-20 ruling).
  const showPromise = !isParent && !task.isMilestone;
  // Progress is what a PROMISED task reports — the server takes it from
  // nobody else (409), so nobody else is offered a slider that cannot save
  // (F-40). A milestone's report is its Done.
  const showProgress = showPromise && task.committed;

  return (
    <section className={styles.root}>
      {/* Once the promise is made, how the work is going is the live question
          and the promise itself becomes reference — so the board goes first and
          the rows read underneath it. Before the commit there is nothing to
          report: an uncommitted task has no progress surface at all. */}
      {showProgress && (
        <TaskProgressSection taskId={taskId} siteId={siteId} scheduleNumber={scheduleNumber} />
      )}
      {showPromise && (
        <TaskPromiseRows
          task={task}
          siteId={siteId}
          scheduleNumber={scheduleNumber}
          onFix={() => openCommitDialog(task.id)}
          areasOpen={areasOpen}
          onToggleAreas={() => setAreasOpen((open) => !open)}
        />
      )}
      <TaskStepHint task={task} step={step} />
    </section>
  );
}
